import {
  Dispatch,
  HTMLAttributes,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { usePlatformSelect, useStoreInfoSelect } from '../ImplementData/components/Filter/Provider';
import { Button, Checkbox, Modal, ModalProps, Radio, Select, Tag, Tooltip } from 'antd';
import { QuickRangePicker } from 'components/QuickRangePicker';
import { useTranslate } from '@tolgee/react';
import { last30days, last7days } from '../ImplementData/utils';
import dayjs, { Dayjs } from 'dayjs';
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import ItemSelect from './ItemSelect';
import { What } from 'views/Aide/Scenario/PolicyTable';
import { uniq } from 'lodash-es';
import { exportNegative, exportUserVoice } from './service';
import { useExportFile } from 'components/ExportCenter/hooks/useExportCenter';
import { DownloadOutlined } from '@ant-design/icons';
import useModal from 'hooks/useModal';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';

const supportPlatforms: General.PlatformUpperCase[] = [
  'SHOPEE',
  'LAZADA',
  'MERCADO',
  'TIKTOK',
  'TIKTOK_US',
];

interface Props {
  isVoice?: boolean;
  departments: Dashboard.Department[];
  value: ReviewAnalysis.OverviewFilter;
  onChange: Dispatch<SetStateAction<ReviewAnalysis.OverviewFilter>>;
}

function useFilter({ isVoice, departments, value, onChange }: Props) {
  const { platform, storeInfo, stars, startTime, endTime, itemRange } = value;

  const { t } = useTranslate();

  const [itemSelectKey, setItemSelectKey] = useState(0);

  const updateValue = useCallback(
    (partial: Partial<ReviewAnalysis.OverviewFilter>, updateItemSelectKey = true) => {
      onChange((pre) => ({
        ...pre,
        ...partial,
      }));
      if (updateItemSelectKey) {
        setItemSelectKey((pre) => pre + 1);
      }
    },
    [onChange],
  );

  const { regionInfo, platformOptions, platformSelect } = usePlatformSelect({
    scene: 'ORDER_STAT',
    showPlatformAll: true,
    departments,
    value: platform || 'ALL',
    platforms: supportPlatforms,
    onChange: (value) => {
      updateValue({
        platform: value === 'ALL' ? undefined : value,
        storeInfo: {},
        spuKeys: [],
      });
    },
  });

  const { storeInfoSelect } = useStoreInfoSelect({
    departments,
    regionInfo,
    platform,
    platformOptions,
    storeInfo,
    onChange: (storeInfo) =>
      updateValue({
        storeInfo,
        spuKeys: [],
      }),
    scene: 'ORDER_STAT',
  });

  const starsSelect = useMemo(() => {
    return (
      <Select
        allowClear
        mode="multiple"
        placeholder={t('选择星级')}
        style={{ width: 120 }}
        value={stars}
        onChange={(stars) => updateValue({ stars, spuKeys: [] })}
        options={new Array(isVoice ? 5 : 3)
          .fill('')
          .map((_, index) => ({ value: index + 1, label: t('review.x星', { count: index + 1 }) }))}
        tagRender={({ value, label, onClose }) => {
          if (stars.length === 1) {
            return (
              <Tag closable onClose={onClose}>
                {label}
              </Tag>
            );
          }
          if (value === stars[0]) {
            return (
              <Tag closable onClose={() => updateValue({ stars: [], spuKeys: [] })}>
                {t('aide.已选x项', { x: stars.length })}
              </Tag>
            );
          }
          return <></>;
        }}
      />
    );
  }, [updateValue, stars, t, isVoice]);

  const itemSelect = (
    <ItemSelect
      key={itemSelectKey}
      filter={value}
      value={value.spuKeys}
      onChange={(spuKeys) => updateValue({ spuKeys }, false)}
    />
  );

  const [dates, setDates] = useState<[Dayjs | undefined, Dayjs | undefined] | undefined>();

  const rangeSelect = useMemo(() => {
    return (
      <QuickRangePicker
        quickActions={
          isVoice
            ? []
            : [
                {
                  key: 'last7days',
                  label: t('remind.近x天', { x: 7 }),
                  range: [last7days()[0].startOf('days'), last7days()[1].endOf('days')],
                },
                {
                  key: 'last30days',
                  label: t('remind.近x天', { x: 30 }),
                  range: [last30days()[0].startOf('days'), last30days()[1].endOf('days')],
                },
              ]
        }
        value={[dayjs(Number(startTime)), dayjs(Number(endTime))]}
        disabledDate={(date) => {
          if (
            date.isSameOrAfter(dayjs(), 'days') ||
            date.isBefore(dayjs().subtract(180, 'days').startOf('days'), 'days')
          ) {
            return true;
          }
          if (dates) {
            const [start, end] = dates;
            const tooLate = start && date.diff(start, 'days') > 180;
            const tooEarly = end && end.diff(date, 'days') > 180;
            return Boolean(tooEarly) || Boolean(tooLate);
          }
          return false;
        }}
        dropdownClassName={cls}
        onCalendarChange={setDates as any}
        onChange={(value) => {
          const [start, end] = value || [];
          if (start) {
            updateValue({
              startTime: start.startOf('days').valueOf().toString(),
              endTime: (end ?? start).endOf('days').valueOf().toString(),
              spuKeys: [],
            });
          }
        }}
        onOpenChange={(open) => {
          if (open) {
            setDates(undefined);
          }
        }}
      />
    );
  }, [dates, endTime, updateValue, startTime, t, isVoice]);

  const itemRangeRadio = isVoice && (
    <section>
      <Radio.Group
        value={itemRange}
        onChange={({ target: { value } }) => updateValue({ itemRange: value, spuKeys: [] })}
      >
        <Radio.Button value="ALL">{t('aide.全部商品')}</Radio.Button>
        <Radio.Button value="RECENTLY_CREATE">
          {t('近期上架')}
          <Tooltip title={t('近30天上架的商品')}>
            <What />
          </Tooltip>
        </Radio.Button>
        <Radio.Button value="TOP_SALE">
          {t('近期高销量')}
          <Tooltip title={t('从上月到今日销量排行在前100且销量不小于10的商品')}>
            <What />
          </Tooltip>
        </Radio.Button>
      </Radio.Group>
    </section>
  );

  return {
    itemRangeRadio,
    platformSelect,
    storeInfoSelect,
    starsSelect,
    itemSelect,
    rangeSelect,
  };
}

function Filter({
  isVoice,
  departments,
  value,
  onChange,
  ...otherProps
}: Props & Omit<HTMLAttributes<HTMLDivElement>, 'onChange'>) {
  const { t } = useTranslate();

  const { itemRangeRadio, platformSelect, storeInfoSelect, starsSelect, itemSelect, rangeSelect } =
    useFilter({
      isVoice,
      departments,
      value,
      onChange,
    });

  const { visible, show, hide } = useModal();

  const { whiteListConfig: { ENABLE_REVIEW_ANALYZER_NEGATIVE_EXPORT } = {} } =
    useOrgConfigStateValue();

  return (
    <Container {...otherProps}>
      {itemRangeRadio}
      <div>
        <section>
          {platformSelect}
          {storeInfoSelect}
          {starsSelect}
          {itemSelect}
        </section>
        <section>
          {ENABLE_REVIEW_ANALYZER_NEGATIVE_EXPORT && (
            <Tooltip title={t('export_data')}>
              <Button icon={<DownloadOutlined />} onClick={show} />
            </Tooltip>
          )}
          <ExportModal
            visible={visible}
            onCancel={hide}
            isVoice={isVoice}
            departments={departments}
            value={value}
          />
          {rangeSelect}(UTC+8:00)
        </section>
      </div>
    </Container>
  );
}

const commonSheets = ['数据总览', '店铺天级数据', '评价明细'];
const subSheets = ['商品相关', '物流配送', '服务体验', '活动营销'];

function ExportModal({
  isVoice,
  departments,
  value: propValue,
  ...otherProps
}: Omit<Props, 'onChange'> & ModalProps) {
  const { t } = useTranslate();

  const [value, setValue] = useState<ReviewAnalysis.OverviewFilter>(propValue);
  useEffect(() => {
    setValue(propValue);
  }, [propValue]);

  const { platformSelect, storeInfoSelect, starsSelect, itemSelect, rangeSelect } = useFilter({
    isVoice,
    departments,
    value,
    onChange: setValue,
  });

  const groups = isVoice ? ['问题发现', '优点总结'] : ['差评归因'];

  const getInitSheetNames = useCallback(
    () => [
      ...commonSheets,
      ...subSheets.map(
        (sheet) =>
          `${
            isVoice ? (propValue.sentimentType === 1 ? '优点总结' : '问题发现') : '差评归因'
          }-${sheet}`,
      ),
    ],
    [isVoice, propValue.sentimentType],
  );

  const [sheetNames, setSheetNames] = useState(getInitSheetNames);
  useEffect(() => {
    setSheetNames(getInitSheetNames());
  }, [getInitSheetNames]);

  const allSheets = [
    ...commonSheets,
    ...groups.map((group) => subSheets.map((sheet) => `${group}-${sheet}`)).flat(),
  ];
  const allChecked = allSheets.every((_) => sheetNames.includes(_));

  const { run: onDownload, loading } = useExportFile();

  return (
    <Modal
      title={t('export_data')}
      width={800}
      {...otherProps}
      okText={t('导出')}
      okButtonProps={{
        loading,
        onClick: (e) =>
          onDownload(() =>
            (isVoice ? exportUserVoice : exportNegative)({
              ...value,
              sheetNames,
            }).then(() => {
              otherProps.onCancel?.(e);
            }),
          ),
      }}
    >
      <ExportContainer>
        <section>
          <div>
            {platformSelect}
            {storeInfoSelect}
            {starsSelect}
            {itemSelect}
          </div>
          <div>
            {t('日期')}(UTC+8:00)
            {rangeSelect}
          </div>
        </section>
        <section>
          <div>
            <Checkbox
              checked={allChecked}
              indeterminate={!allChecked && sheetNames.length > 0}
              onChange={({ target: { checked } }) => setSheetNames(checked ? allSheets : [])}
            >
              {t('全部数据')}
            </Checkbox>
          </div>
          <ul>
            <li>
              <div>
                {commonSheets.map((sheet) => (
                  <Checkbox
                    key={sheet}
                    checked={sheetNames.includes(sheet)}
                    onChange={({ target: { checked } }) =>
                      setSheetNames(
                        checked ? [...sheetNames, sheet] : sheetNames.filter((_) => _ !== sheet),
                      )
                    }
                  >
                    {t(sheet)}
                  </Checkbox>
                ))}
              </div>
            </li>
            {groups.map((group) => {
              const allSheets = subSheets.map((sheet) => `${group}-${sheet}`);
              const allChecked = allSheets.every((_) => sheetNames.includes(_));
              return (
                <li key={group}>
                  <div>
                    <Checkbox
                      checked={allChecked}
                      indeterminate={!allChecked && allSheets.some((_) => sheetNames.includes(_))}
                      onChange={({ target: { checked } }) =>
                        setSheetNames(
                          checked
                            ? uniq([...sheetNames, ...allSheets])
                            : sheetNames.filter((_) => !allSheets.includes(_)),
                        )
                      }
                    >
                      {t(group)}
                    </Checkbox>
                  </div>
                  <div>
                    {subSheets.map((sheet) => {
                      const sheetName = `${group}-${sheet}`;
                      return (
                        <Checkbox
                          key={sheet}
                          checked={sheetNames.includes(sheetName)}
                          onChange={({ target: { checked } }) =>
                            setSheetNames(
                              checked
                                ? [...sheetNames, sheetName]
                                : sheetNames.filter((_) => _ !== sheetName),
                            )
                          }
                        >
                          {t(sheet)}
                        </Checkbox>
                      );
                    })}
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      </ExportContainer>
    </Modal>
  );
}

const cls = css`
  .ant-picker-cell-disabled {
    pointer-events: auto;
    cursor: not-allowed;
  }
`;

const Container = styled.header`
  > section {
    display: flex;
    align-items: center;
    height: 56px;
    padding: 0 14px;
    border-bottom: solid 1px var(--fett-border-default);
    background: var(--fett-bg-panel);
  }
  > div {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
    padding: 10px 14px;
    background: var(--fett-bg-panel);
    > section {
      display: flex;
      align-items: center;
      > * {
        margin-right: 14px;
      }
    }
  }
`;

const ExportContainer = styled.div`
  > section {
    border: solid 1px #f0f2f5;
    border-radius: 4px;
    & + section {
      margin-top: 16px;
      > div {
        height: 46px;
      }
    }
    > div {
      display: flex;
      align-items: center;
      gap: 16px;
      height: 56px;
      padding: 0 16px;
      &:first-of-type {
        border-bottom: solid 1px #f0f2f5;
      }
    }
    > ul {
      margin: 0;
      padding: 16px;
      list-style: none;
      > li {
        border: solid 1px #f0f2f5;
        border-radius: 4px;
        & + li {
          margin-top: 16px;
        }
        > div {
          display: flex;
          align-items: center;
          height: 46px;
          padding: 0 16px;
          & + div {
            height: 56px;
            border-top: solid 1px #f0f2f5;
          }
          > * {
            flex: 0 0 25%;
          }
        }
      }
    }
  }
`;

export default Filter;
